import { CommonModule } from '@angular/common';
import { Component, input, computed, ViewEncapsulation, signal, inject, output } from '@angular/core';
import { MetricCardComponent } from 'src/app/core/components/metric-card';
import { ConfigStatusResponse, ConfigurationStatusDetailResponse } from 'src/app/core/models/analytics/es-models';
import { EsConfigDetailsDialogComponent } from './components/es-config-details-dialog.component';
import { EsAnalyticsService } from '../../services/es-analytics.service';
import { BaseComponent } from 'src/app/core/shared/common/base.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-es-config-status-card',
  standalone: true,
  imports: [CommonModule, MetricCardComponent, EsConfigDetailsDialogComponent],
  templateUrl: './es-config-status-card.component.html',
  styleUrl: './es-config-status-card.component.scss',
})
export class EsConfigStatusCardComponent extends BaseComponent {
  configStatus = input<ConfigStatusResponse>();
  organizationId = input<number | null>(null);
  
  // TEMPORARY: Output to notify parent when config changes (for prototyping with mock data)
  // TODO: Remove when real API is implemented - parent will get updates via normal data refresh
  configChanged = output<void>();

  private esAnalyticsService = inject(EsAnalyticsService);

  // Dialog state
  isDialogVisible = signal<boolean>(false);
  isLoadingDetails = signal<boolean>(false);
  configurationDetails = signal<ConfigurationStatusDetailResponse | null>(null);

  // Computed property to determine the left border color based on configuration status
  leftBorderColor = computed(() => {
    const status = this.configStatus()?.config_status?.toLowerCase();

    switch (status) {
      case 'fully configured':
        return '#198754'; // Bootstrap success green
      case 'mostly configured':
      case 'partially configured':
        return '#fd7e14'; // Bootstrap warning orange
      case 'configuration required':
      case 'not configured':
        return '#dc3545'; // Bootstrap danger red
      default:
        return '#6c757d'; // Bootstrap secondary gray
    }
  });

  // Computed property to determine if we should show the left border
  showBorder = computed(() => {
    return !!this.configStatus()?.config_status;
  });

  constructor() {
    super();
  }

  // Handle card click to open dialog
  onCardClick(): void {
    this.isDialogVisible.set(true);
    this.loadConfigurationDetails();
  }

  // Load configuration details from service
  private loadConfigurationDetails(): void {
    this.isLoadingDetails.set(true);
    
    this.esAnalyticsService.getConfigurationDetails(this.organizationId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.configurationDetails.set(response);
          this.isLoadingDetails.set(false);
        },
        error: (error) => {
          console.error('Failed to load configuration details:', error);
          this.configurationDetails.set(null);
          this.isLoadingDetails.set(false);
        }
      });
  }

  // Handle saving changes from dialog
  onSaveChanges(changes: {itemId: string, accepted: boolean, note?: string}[]): void {
    // Apply changes sequentially for simplicity
    let updateCount = 0;
    
    changes.forEach(change => {
      this.esAnalyticsService.updateConfigurationDeviation(
        this.organizationId(), 
        change.itemId, 
        change.accepted, 
        change.note
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          updateCount++;
          if (updateCount === changes.length) {
            // All updates completed, reload configuration details and notify parent
            this.loadConfigurationDetails();
            // TEMPORARY: Notify parent to refresh config status (for prototyping)
            this.configChanged.emit();
          }
        },
        error: (error) => {
          console.error('Failed to save configuration change:', error);
        }
      });
    });
  }

  // Handle dialog close
  onDialogClose(): void {
    this.isDialogVisible.set(false);
    this.configurationDetails.set(null);
  }
}
